import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { searchContent } from '../../utils/searchUtils';
import SearchResults from './SearchResults';

const SearchBar = ({ t, scrollToSection, language }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Close search when clicking outside
  const handleClickOutside = useCallback((event) => {
    if (searchRef.current && !searchRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const handleSearchChange = useCallback((e) => {
    const query = e.target.value;
    setSearchQuery(query);
    const results = searchContent(query, t);
    setSearchResults(results);
  }, [t]);

  const handleResultClick = useCallback((result) => {
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => scrollToSection(result.section), 100);
    } else {
      scrollToSection(result.section);
    }
    setIsOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  }, [navigate, scrollToSection]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  }, []);

  return (
    <div ref={searchRef} className="relative">
      {!isOpen ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsOpen(true)}
          className="gap-2"
          aria-label="Search"
        >
          <Search className="h-4 w-4" />
        </Button>
      ) : (
        <div className="absolute right-0 top-0 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="p-3 border-b border-gray-200 flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={handleSearchChange}
              autoFocus
              className="border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="p-1 h-auto"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <SearchResults
            results={searchResults}
            query={searchQuery}
            onResultClick={handleResultClick}
          />
        </div>
      )}
    </div>
  );
};

export default SearchBar;